import React from 'react';
import { cn } from './cn';
import { aileToplami, hexToRgba, MUFREDATLAR } from '../../lib/dersler';

// Ders seçici — "ÖSYM kitapçık bölümleri" (2026-10-21, kullanıcı kararı). Dersler basılı
// kitapçıktaki test bloklarının altında, ÖSYM sırasında durur; bant rengi o testin
// kitapçıktaki tema rengidir. Ayna: backend/src/lib/dersBaslik.js OSYM_BLOK_RENK.
const BANT = {
  lila: { zemin: '#DFCCE4', kenar: '#C7A9D0' },
  camgobegi: { zemin: '#BEE5EB', kenar: '#8FCCD6' },
  somon: { zemin: '#FCD1C2', kenar: '#EFAA93' },
  acikMavi: { zemin: '#BCE4F7', kenar: '#8CCBEA' },
  diger: { zemin: '#E2E8F0', kenar: '#CBD5E1' },
};

export const SINAV_BOLUMLERI = {
  TYT: [
    { ad: 'Türkçe', bant: 'lila', dersler: ['tyt_turkce'] },
    { ad: 'Sosyal Bilimler', bant: 'camgobegi', dersler: ['tyt_tarih', 'tyt_cografya', 'tyt_felsefe', 'tyt_din'] },
    { ad: 'Temel Matematik', bant: 'somon', dersler: ['tyt_matematik', 'tyt_geometri'] },
    { ad: 'Fen Bilimleri', bant: 'camgobegi', dersler: ['tyt_fizik', 'tyt_kimya', 'tyt_biyoloji'] },
  ],
  AYT: [
    { ad: 'Türk Dili ve Edebiyatı–Sosyal Bilimler-1', bant: 'camgobegi', dersler: ['ayt_edebiyat', 'ayt_tarih', 'ayt_cografya'], not: 'Tarih ve Coğrafya, Sosyal Bilimler-2 testinde de yer alır.' },
    { ad: 'Sosyal Bilimler-2', bant: 'lila', dersler: ['ayt_felsefe', 'ayt_mantik', 'ayt_psikoloji', 'ayt_sosyoloji', 'ayt_din'] },
    { ad: 'Matematik', bant: 'somon', dersler: ['ayt_matematik', 'ayt_geometri'] },
    { ad: 'Fen Bilimleri', bant: 'acikMavi', dersler: ['ayt_fizik', 'ayt_kimya', 'ayt_biyoloji'] },
  ],
};

// Görünür dersleri bölümlere dağıtır. Görünür dersi kalmayan bölüm düşer; hiçbir
// bölümde olmayan ders (ileride eklenen) "Diğer dersler"e gider, kaybolmaz.
export function bolumlereAyir(sinav, aileler) {
  const tanimli = SINAV_BOLUMLERI[sinav] || [];
  const kullanilan = new Set();
  const bolumler = tanimli.map((b) => {
    const uyeler = b.dersler.map((k) => aileler.find((a) => a.key === k)).filter(Boolean);
    uyeler.forEach((a) => kullanilan.add(a.key));
    return { ...b, aileler: uyeler };
  }).filter((b) => b.aileler.length > 0);
  const kalan = aileler.filter((a) => !kullanilan.has(a.key));
  if (kalan.length) bolumler.push({ ad: 'Diğer dersler', bant: 'diger', aileler: kalan });
  return bolumler;
}

// Ders kutusunun sayıları. mod: 'gizle' (satılanlar düşülür) | 'goster' (satılan payı
// ayrıca) | null (satılan bilgisi yok — toplam). ozet: ders-ozeti yanıtı
// { sayilar, maarif, satilan, satilanMaarif }.
export function dersKutuSayilari(aile, ozet, mod) {
  const toplam = aileToplami(aile, ozet.sayilar);
  const maarifTum = aileToplami(aile, ozet.maarif);
  const sat = mod ? aileToplami(aile, ozet.satilan) : 0;
  const satMaarif = mod ? aileToplami(aile, ozet.satilanMaarif) : 0;
  return {
    sayi: mod === 'gizle' ? toplam - sat : toplam,
    meb: Math.max(0, toplam - maarifTum - (sat - satMaarif)),
    maarif: Math.max(0, maarifTum - satMaarif),
    satilan: mod === 'goster' ? sat : 0,
  };
}

const sayi = (n) => Number(n || 0).toLocaleString('tr-TR');
const MEB_RENK = MUFREDATLAR[0].renk;
const MAARIF_RENK = MUFREDATLAR[1].renk;
const SATILAN_DESEN = 'repeating-linear-gradient(135deg, #EA580C 0px, #EA580C 2px, #FDBA74 2px, #FDBA74 4px)';

// Elle çizilmiş çizgi ikonlar (24×24, stroke = ders rengi). Emoji her işletim sisteminde
// farklı çizildiği için yerine geçti.
const IKON = {
  matematik: <><path d="M4 4v16h16" /><path d="M7 16c2.5-7 5-9 7-6s3 2 5-4" /></>,
  geometri: <><path d="M4 19h16L9 5Z" /><path d="M9 5v14" /><path d="M9 16h3v3" /></>,
  kimya: <><path d="M9 3h6" /><path d="M10 3v6.5L4.6 18.2A1.8 1.8 0 0 0 6.1 21h11.8a1.8 1.8 0 0 0 1.5-2.8L14 9.5V3" /><path d="M7 15h10" /></>,
  fizik: <><circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" /><ellipse cx="12" cy="12" rx="9.5" ry="3.8" /><ellipse cx="12" cy="12" rx="9.5" ry="3.8" transform="rotate(60 12 12)" /><ellipse cx="12" cy="12" rx="9.5" ry="3.8" transform="rotate(-60 12 12)" /></>,
  biyoloji: <><path d="M7 3c0 4.5 10 4.5 10 9s-10 4.5-10 9" /><path d="M17 3c0 4.5-10 4.5-10 9s10 4.5 10 9" /><path d="M8 12h8M8.5 4.5h7M8.5 19.5h7" /></>,
  turkce: <><path d="M12 6.5C10 5 6.5 4.6 3.5 5.3V19c3-.7 6.5-.3 8.5 1.2 2-1.5 5.5-1.9 8.5-1.2V5.3c-3-.7-6.5-.3-8.5 1.2Z" /><path d="M12 6.5v13.7" /></>,
  edebiyat: <><path d="M19.5 4.5C12 4.5 8 9.5 7 16" /><path d="M19.5 4.5c.5 6.5-4.5 11-12 11.5" /><path d="M4.5 20 7 16" /></>,
  tarih: <><path d="M3.5 20.5h17" /><path d="M5 17.5h14" /><path d="M4 8h16L12 3.5Z" /><path d="M7 8v9.5M12 8v9.5M17 8v9.5" /></>,
  cografya: <><circle cx="12" cy="12" r="8.5" /><path d="M3.5 12h17" /><path d="M12 3.5c2.6 2.4 3.8 5.2 3.8 8.5s-1.2 6.1-3.8 8.5c-2.6-2.4-3.8-5.2-3.8-8.5S9.4 5.9 12 3.5Z" /></>,
  felsefe: <><path d="M5 19V6.5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H8.5Z" /><path d="M10.3 8.4a1.8 1.8 0 1 1 2.6 1.6c-.6.3-.9.8-.9 1.4" /><circle cx="12" cy="13.4" r=".5" fill="currentColor" /></>,
  din: <><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" /><path d="M18 3.5v3M16.5 5h3" /></>,
  mantik: <><circle cx="12" cy="5" r="2" /><circle cx="6" cy="18" r="2" /><circle cx="18" cy="18" r="2" /><path d="M11 6.8 7 16.2M13 6.8l4 9.4M8 18h8" /></>,
  psikoloji: <><path d="M9 21v-3.5A7 7 0 1 1 18.5 11l1.5 3h-2v2.5a1.5 1.5 0 0 1-1.5 1.5H15v3" /><circle cx="12.5" cy="9.5" r="2" /></>,
  // LGS (2026-10-31): fen = deney tüpü + kabarcık, inkılap = bayrak, ingilizce = konuşma balonu.
  fen: <><path d="M9 3h6" /><path d="M10 3v12a2 2 0 0 0 4 0V3" /><path d="M10 10h4" /><circle cx="17.5" cy="7" r="1.5" /><circle cx="18.5" cy="12" r="1" /></>,
  inkilap: <><path d="M5 21V3.5" /><path d="M5 4h12.5l-2.5 4 2.5 4H5" /></>,
  ingilizce: <><path d="M4 5h16v11H10l-4 3.5V16H4Z" /><path d="M9.5 13.5 12 7.5l2.5 6M10.4 11.5h3.2" /></>,
  sosyoloji: <><circle cx="9" cy="8" r="3" /><path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" /><circle cx="16.5" cy="9" r="2.4" /><path d="M15 14.2a4.5 4.5 0 0 1 6 4.3" /></>,
};

export function DersIkon({ dersKey, className, style }) {
  const ad = String(dersKey || '').replace(/^(tyt|ayt|lgs)_/, '');
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true" className={className} style={style}>
      {IKON[ad] || <circle cx="12" cy="12" r="7" />}
    </svg>
  );
}

function Cubuk({ meb, maarif, satilan }) {
  const t = meb + maarif + satilan;
  const pay = (n) => (t ? `${(n / t) * 100}%` : '0%');
  const dilim = 'h-full transition-[width] duration-300 motion-reduce:transition-none';
  return (
    <span className="flex h-1 w-full overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
      <span className={dilim} style={{ width: pay(meb), backgroundColor: MEB_RENK }} />
      <span className={dilim} style={{ width: pay(maarif), backgroundColor: MAARIF_RENK }} />
      <span className={dilim} style={{ width: pay(satilan), backgroundImage: SATILAN_DESEN }} />
    </span>
  );
}

function DersHucresi({ aile, s, secili, onSec }) {
  const pasif = !aile.aktif;
  const etiket = `${aile.label}: ${sayi(s.sayi)} soru (MEB 2018 ${sayi(s.meb)}, Maarif ${sayi(s.maarif)}`
    + (s.satilan ? `, ${sayi(s.satilan)} satılan)` : ')');
  return (
    <button
      type="button"
      data-ders={aile.key}
      disabled={pasif}
      aria-pressed={secili}
      aria-label={etiket}
      title={pasif ? `${aile.label} — yakında` : etiket}
      onClick={() => !pasif && onSec(aile.key)}
      className={cn(
        'relative flex min-w-0 flex-col gap-1 border-l border-t border-slate-200/80 px-2.5 pb-1.5 pt-1.5 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset',
        pasif ? 'cursor-not-allowed bg-slate-50/70' : secili ? '' : 'bg-white hover:bg-slate-50'
      )}
      style={{
        ...(secili && !pasif ? { backgroundColor: hexToRgba(aile.renk, 0.08) } : null),
        '--tw-ring-color': hexToRgba(aile.renk, 0.55),
      }}
    >
      {/* Sıkı düzen (2026-10-31, kullanıcı: "dikeyde daha az yer, kartlar küçülsün"):
          1. satır ikon + ad + satılan · 2. satır sayı + 2018/Maarif sayıları · 3. satır ince çubuk.
          Dar hücreye (TYT'nin 10 dersi tek sırada) sığar; ad kesilmez. */}
      <span className="flex min-w-0 items-center gap-1.5">
        <DersIkon dersKey={aile.key} className={cn('h-3.5 w-3.5 shrink-0', pasif && 'opacity-40')} style={{ color: aile.renk }} />
        <span className="min-w-0 flex-1 truncate text-[12px] font-semibold leading-tight" style={{ color: secili && !pasif ? aile.renk : pasif ? '#94A3B8' : '#334155' }}>
          {aile.label}
        </span>
        {!pasif && s.satilan > 0 && (
          <span data-testid="ders-satilan" className="shrink-0 text-[10px] font-semibold leading-none tabular-nums text-orange-600">−{sayi(s.satilan)}</span>
        )}
      </span>
      {pasif ? (
        <span className="text-[11px] leading-tight text-slate-400">yakında</span>
      ) : (
        <>
          <span className="flex items-baseline justify-between gap-1.5">
            <span className="text-[15px] font-bold leading-none tabular-nums text-slate-800">{sayi(s.sayi)}</span>
            <span className="flex gap-1 text-[10px] font-semibold leading-none tabular-nums">
              {s.meb > 0 && <span style={{ color: MEB_RENK }}>{sayi(s.meb)}</span>}
              {s.meb > 0 && s.maarif > 0 && <span className="text-slate-300">·</span>}
              {s.maarif > 0 && <span style={{ color: MAARIF_RENK }}>{sayi(s.maarif)}</span>}
            </span>
          </span>
          <Cubuk meb={s.meb} maarif={s.maarif} satilan={s.satilan} />
        </>
      )}
      {secili && !pasif && <span className="absolute inset-x-0 bottom-0 h-0.5" style={{ backgroundColor: aile.renk }} />}
    </button>
  );
}

// Lejant: çubuğun dilimlerini adlandırır. Satılan dilimi yalnız "göster" modunda var.
export function BolumLejanti({ mod }) {
  const kutu = 'inline-block h-2 w-3 rounded-sm';
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
      <span className="flex items-center gap-1.5"><span className={kutu} style={{ backgroundColor: MEB_RENK }} />MEB 2018</span>
      <span className="flex items-center gap-1.5"><span className={kutu} style={{ backgroundColor: MAARIF_RENK }} />Maarif 2024</span>
      {mod === 'goster' && (
        <span className="flex items-center gap-1.5"><span className={kutu} style={{ backgroundImage: SATILAN_DESEN }} />satılan</span>
      )}
    </span>
  );
}

export function SinavBolumleri({ sinav, aileler, aktifKey, onSec, ozet, satilanModu = null }) {
  return (
    <div className="flex flex-wrap gap-2.5">
      {bolumlereAyir(sinav, aileler).map((b) => {
        const n = b.aileler.length;
        const bant = BANT[b.bant] || BANT.diger;
        const kutular = b.aileler.map((a) => [a, dersKutuSayilari(a, ozet, satilanModu)]);
        const toplam = kutular.reduce((t, [a, s]) => t + (a.aktif ? s.sayi : 0), 0);
        return (
          <section
            key={b.ad}
            className="min-w-0 overflow-hidden rounded-lg border bg-white"
            style={{ flex: `${n} 1 ${n * 104}px`, borderColor: bant.kenar }}
          >
            <header className="flex items-baseline justify-between gap-3 px-2.5 py-0.5" style={{ backgroundColor: bant.zemin }} title={b.not}>
              <h3 className="truncate text-[11px] font-bold uppercase tracking-wide text-slate-700">{b.ad}</h3>
              <span className="shrink-0 text-[11px] font-semibold tabular-nums text-slate-700">{sayi(toplam)}</span>
            </header>
            {/* Sol/üst kenarlar -1px kaydırılıp kırpılır: hücre ayraçları yalnız hücreler
                ARASINDA kalır, sarma olsa da dış çerçeveyle çift çizgi oluşmaz. */}
            <div className="overflow-hidden">
              <div className="-ml-px -mt-px grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))' }}>
                {kutular.map(([a, s]) => (
                  <DersHucresi key={a.key} aile={a} s={s} secili={a.key === aktifKey} onSec={onSec} />
                ))}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default SinavBolumleri;
